import { useEffect, useMemo, useState } from "react";
import { getFirestore, collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import app from "./firebase";
import styles from "./CombosDashboard.module.css";

const db = getFirestore(app);

const emptyForm = { name: "", description: "", products: [] };

export default function CombosDashboard() {
  const [combos, setCombos] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editCombo, setEditCombo] = useState(null);

  const [form, setForm] = useState(emptyForm);
  const [tempProduct, setTempProduct] = useState({ productId: "", quantity: "" });
  const [showProductSelector, setShowProductSelector] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [comboSnap, productSnap] = await Promise.all([
        getDocs(collection(db, "combos")),
        getDocs(collection(db, "products")),
      ]);

      setCombos(comboSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setProducts(productSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching combos/products:", err);
      alert("Failed to fetch combos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openAdd = () => {
    setForm(emptyForm);
    setTempProduct({ productId: "", quantity: "" });
    setEditCombo(null);
    setShowAdd(true);
  };

  const openEdit = (combo) => {
    setEditCombo(combo);
    setForm({
      name: combo?.name || "",
      description: combo?.description || "",
      products: Array.isArray(combo?.products) ? combo.products : [],
    });
    setTempProduct({ productId: "", quantity: "" });
    setShowEdit(true);
  };

  const closeModal = () => {
    setShowAdd(false);
    setShowEdit(false);
    setEditCombo(null);
    setForm(emptyForm);
    setTempProduct({ productId: "", quantity: "" });
    setShowProductSelector(false);
  };

  const selectProduct = (product) => {
    setTempProduct((prev) => ({ ...prev, productId: product.id }));
    setShowProductSelector(false);
  };

  const selectedTempProductName = useMemo(() => {
    const p = products.find((x) => x.id === tempProduct.productId);
    return p ? p.name : "Select a product...";
  }, [products, tempProduct.productId]);

  const addProductToCombo = () => {
    if (!tempProduct.productId || !tempProduct.quantity) {
      alert("Please select a product and enter quantity");
      return;
    }

    const product = products.find((p) => p.id === tempProduct.productId);
    if (!product) {
      alert("Product not found");
      return;
    }

    const qty = parseInt(tempProduct.quantity, 10);
    if (!Number.isFinite(qty) || qty <= 0) {
      alert("Quantity must be a positive number");
      return;
    }

    const existingIndex = (form.products || []).findIndex(
      (p) => p.productId === tempProduct.productId
    );

    if (existingIndex !== -1) {
      const updatedProducts = [...form.products];
      updatedProducts[existingIndex] = {
        ...updatedProducts[existingIndex],
        quantity: qty,
      };
      setForm((prev) => ({ ...prev, products: updatedProducts }));
    } else {
      setForm((prev) => ({
        ...prev,
        products: [
          ...(prev.products || []),
          {
            productId: tempProduct.productId,
            productName: product.name,
            quantity: qty,
          },
        ],
      }));
    }

    setTempProduct({ productId: "", quantity: "" });
  };

  const removeProductFromCombo = (productId) => {
    setForm((prev) => ({
      ...prev,
      products: (prev.products || []).filter((p) => p.productId !== productId),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !(form.products || []).length) {
      alert("Please enter combo name and add at least one product");
      return;
    }

    try {
      const nowIso = new Date().toISOString();
      const comboData = {
        name: form.name,
        description: form.description || "",
        products: form.products || [],
        updatedAt: nowIso,
      };

      if (editCombo?.id) {
        await updateDoc(doc(db, "combos", editCombo.id), comboData);
      } else {
        await addDoc(collection(db, "combos"), {
          ...comboData,
          createdAt: nowIso,
        });
      }

      closeModal();
      fetchData();
    } catch (err) {
      console.error("Error saving combo:", err);
      alert("Failed to save combo");
    }
  };

  const handleDelete = async (comboId) => {
    if (!window.confirm("Delete this combo?")) return;
    try {
      await deleteDoc(doc(db, "combos", comboId));
      fetchData();
    } catch (err) {
      console.error("Error deleting combo:", err);
      alert("Failed to delete combo");
    }
  };

  const filteredCombos = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return combos
      .filter((c) => {
        const name = (c.name || "").toLowerCase();
        const desc = (c.description || "").toLowerCase();
        return name.includes(term) || desc.includes(term);
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [combos, searchTerm]);

  if (loading) {
    return (
      <div className={styles.dashboardContainer}>
        <div className={styles.loading}>Loading combos...</div>
      </div>
    );
  }

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.header}>
        <h2 className={styles.dashboardTitle}>Combos Management</h2>
        <button className={styles.addButton} onClick={openAdd}>
          + Add Combo
        </button>
      </div>

      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{combos.length}</div>
          <div className={styles.statLabel}>Total Combos</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {combos.reduce((sum, c) => sum + ((c.products || []).length || 0), 0)}
          </div>
          <div className={styles.statLabel}>Combo Line Items</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{products.length}</div>
          <div className={styles.statLabel}>Products Available</div>
        </div>
      </div>

      <div className={styles.searchContainer}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search combos by name or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm ? (
          <button
            className={styles.clearSearch}
            onClick={() => setSearchTerm("")}
            type="button"
            aria-label="Clear search"
          >
            ✕
          </button>
        ) : null}
      </div>

      <div className={styles.cardGrid}>
        {filteredCombos.map((combo) => (
          <div key={combo.id} className={styles.comboCard}>
            <div className={styles.comboHeader}>
              <div className={styles.comboIcon}>📦</div>
              <div className={styles.comboName}>{combo.name}</div>
            </div>

            {combo.description ? (
              <div className={styles.comboDescription}>{combo.description}</div>
            ) : null}

            <div className={styles.productsSection}>
              <div className={styles.productsHeader}>
                <div className={styles.productsCount}>
                  Products ({(combo.products || []).length})
                </div>
              </div>
              {(combo.products || []).length ? (
                <div className={styles.productsList}>
                  {(combo.products || []).map((p) => (
                    <div key={p.productId} className={styles.productRow}>
                      <div className={styles.productName}>{p.productName || p.productId}</div>
                      <div className={styles.productQty}>{p.quantity} packets</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyProducts}>No products added yet</div>
              )}
            </div>

            <div className={styles.actionsRow}>
              <button className={styles.editButton} onClick={() => openEdit(combo)}>
                Edit
              </button>
              <button className={styles.deleteButton} onClick={() => handleDelete(combo.id)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredCombos.length === 0 ? (
        <div className={styles.emptyState}>No combos match your search.</div>
      ) : null}

      {(showAdd || showEdit) ? (
        <div className={styles.modal} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{showEdit ? "Edit Combo" : "Add New Combo"}</h3>
              <button className={styles.modalClose} onClick={closeModal} type="button" aria-label="Close">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label>Combo Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter combo name"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter combo description"
                  rows={3}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Add Products to Combo</label>

                <button
                  type="button"
                  className={styles.selectorButton}
                  onClick={() => setShowProductSelector(true)}
                >
                  <span className={!tempProduct.productId ? styles.placeholderText : ""}>
                    {selectedTempProductName}
                  </span>
                  <span className={styles.chevron}>▾</span>
                </button>

                <div className={styles.quantityRow}>
                  <div className={styles.quantityLabel}>Quantity (packets)</div>
                  <input
                    className={styles.quantityInput}
                    value={tempProduct.quantity}
                    onChange={(e) => setTempProduct((prev) => ({ ...prev, quantity: e.target.value }))}
                    placeholder="0"
                    inputMode="numeric"
                  />
                  <button
                    type="button"
                    className={styles.addLineButton}
                    onClick={addProductToCombo}
                  >
                    + Add
                  </button>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Products in Combo ({(form.products || []).length})</label>
                {(form.products || []).length === 0 ? (
                  <div className={styles.emptyProducts}>No products added yet</div>
                ) : (
                  <div className={styles.selectedProductsList}>
                    {(form.products || []).map((cp) => (
                      <div key={cp.productId} className={styles.selectedProductRow}>
                        <div>
                          <div className={styles.selectedProductName}>{cp.productName || cp.productId}</div>
                          <div className={styles.selectedProductQuantity}>{cp.quantity} packets</div>
                        </div>
                        <button
                          type="button"
                          className={styles.removeButton}
                          onClick={() => removeProductFromCombo(cp.productId)}
                          aria-label="Remove"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.modalActions}>
                <button type="button" className={styles.cancelButton} onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className={styles.saveButton}>
                  Save
                </button>
              </div>
            </form>

            {showProductSelector ? (
              <div className={styles.selectorOverlay} onClick={() => setShowProductSelector(false)}>
                <div className={styles.selectorModal} onClick={(e) => e.stopPropagation()}>
                  <div className={styles.selectorHeader}>
                    <div className={styles.selectorTitle}>Select Product</div>
                    <button
                      type="button"
                      className={styles.modalClose}
                      onClick={() => setShowProductSelector(false)}
                      aria-label="Close product selector"
                    >
                      ×
                    </button>
                  </div>
                  <div className={styles.selectorList}>
                    {products
                      .slice()
                      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                      .map((p) => (
                        <button
                          type="button"
                          key={p.id}
                          className={`${styles.selectorItem} ${
                            tempProduct.productId === p.id ? styles.selectorItemSelected : ""
                          }`}
                          onClick={() => selectProduct(p)}
                        >
                          <span>{p.name}</span>
                          {tempProduct.productId === p.id ? (
                            <span className={styles.checkmark}>✓</span>
                          ) : null}
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
